import React, { useState } from 'react'
import { StyleSheet, TouchableOpacity, View } from 'react-native'
import { Theme } from '@theme'
import { useNavigation } from '@react-navigation/native'
import { useTheme } from '@react-navigation/native'
import { Text, Icon } from '../atoms'
import { getCommonStyles } from '@utils'
import { NoteItem } from '../molecules/NoteItem'
import { Note, useCreationStackIntegration, usePaginateNotes, useScreen } from '@hooks'
import { Button, NoteItemSkeletonList } from '../molecules'
import { useLanguage } from '@context'

export const Notes = ({
  profileId,
  data,
  isGhost = false,
  onNotePress,
  onAddPress,
}: {
  profileId?: string
  data?: Note[]
  isGhost?: boolean
  onNotePress?: (noteId: string) => void
  onAddPress?: (isShared?: boolean) => void
}) => {
  const theme = useTheme()
  const { t } = useLanguage()
  const styles = getStyles(theme)
  const commonStyles = getCommonStyles(theme)
  const navigation = useNavigation()
  const { isMobile } = useScreen()
  const { isCreationStackEnabled, showNoteCreation } = useCreationStackIntegration()

  const {
    flatData: allNotes,
    isLoading: isLoadingNotes,
    isFetched: isFetchedNotes,
  } = usePaginateNotes({ contactId: profileId })
  const notes = data !== undefined ? data : allNotes

  const [isShared, setIsShared] = useState(isGhost ? false : true)
  const [selectedNoteId, setSelectedNoteId] = useState<string | null>(null)

  const handleAddNote = () => {
    // Ghost profiles can't own shared notes — they have no real account
    // to share with. Force private on the way out so EditNote doesn't
    // re-flip it.
    const effectiveIsShared = isGhost ? false : isShared
    if (onAddPress) {
      onAddPress(effectiveIsShared)
    } else if (isCreationStackEnabled) {
      showNoteCreation({
        isCreationStack: true,
        contactId: profileId,
        isGhost,
      })
    } else {
      ;(navigation as any).navigate('Menu', {
        screen: 'Notes',
        params: {
          screen: 'EditNote',
          params: {
            isShared: effectiveIsShared,
            contactId: profileId,
            isGhost,
          },
        },
      })
    }
  }

  return (
    <View style={styles.noteContainer}>
      <View style={styles.row}>
        {!isGhost && (
          <TouchableOpacity
            onPress={() => setIsShared(true)}
            style={[
              styles.rowFlex,
              {
                borderBottomWidth: 2,
                borderBottomColor: isShared ? theme.colors.primary : theme.colors.grey20,
              },
            ]}
          >
            <Icon
              name="user"
              size={theme.iconSize.small}
              color={isShared ? theme.colors.primary : theme.colors.greyBlue}
            />
            <Text type="bodyBold">{t('common.shared')}</Text>
            <Text type="body" color={theme.colors.lightGrey}>
              {t('common.parenCount', { count: notes.filter((n) => n.isShared).length })}
            </Text>
          </TouchableOpacity>
        )}
        <TouchableOpacity
          onPress={() => setIsShared(false)}
          style={[
            styles.rowFlex,
            !isGhost && {
              borderBottomWidth: 2,
              borderBottomColor: isShared ? theme.colors.grey10 : theme.colors.primary,
            },
          ]}
        >
          <Icon
            name="lock"
            size={theme.iconSize.small}
            color={!isShared ? theme.colors.primary : theme.colors.greyBlue}
          />
          <Text type="bodyBold">{t('common.personal')}</Text>
          <Text type="body" color={theme.colors.lightGrey}>
            {t('common.parenCount', { count: notes.filter((n) => !n.isShared).length })}
          </Text>
        </TouchableOpacity>
      </View>
      {!data && (
        <Button
          isSmall
          style={[styles.buttonAdd, !isMobile && commonStyles.alignSelfCenter]}
          title={t('notesForm.addNote')}
          textColor={theme.colors.primary}
          isIconRight
          iconName="edit"
          iconColor={theme.colors.primary}
          onPress={handleAddNote}
        />
      )}
      {!data && isLoadingNotes ? (
        <NoteItemSkeletonList count={5} />
      ) : (
        notes
          .filter((n) => n.isShared === isShared)
          .map((note) => (
            <NoteItem
              key={note.id}
              note={note}
              isSelected={!onNotePress && selectedNoteId === note.id}
              onPress={() => {
                if (onNotePress) {
                  onNotePress(note.id)
                  setSelectedNoteId(note.id)
                } else {
                  ;(navigation as any).navigate('Menu', {
                    screen: 'Notes',
                    params: {
                      screen: 'EditNote',
                      params: {
                        noteId: note.id,
                        contactId: profileId,
                        isGhost,
                      },
                    },
                  })
                }
              }}
            />
          ))
      )}
      {isFetchedNotes && notes.length === 0 && (
        <View style={styles.emptyContainer}>
          <Text type="body" style={styles.emptyText}>
            {isShared ? t('notes.empty.titleShared') : t('notes.empty.titlePersonal')}
          </Text>
          <Text type="body" style={styles.emptySubtext}>
            {t('notes.empty.pressPlusToCreate')}
          </Text>
        </View>
      )}
    </View>
  )
}

const getStyles = (theme: Theme) =>
  StyleSheet.create({
    noteContainer: {
      padding: theme.spacing.lg,
    },
    row: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      paddingBottom: theme.spacing.sm,
    },
    rowFlex: {
      flex: 1,
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      gap: theme.spacing.xs,
      paddingBottom: theme.spacing.sm,
    },
    emptyContainer: {
      flex: 1,
      alignItems: 'center',
      justifyContent: 'center',
      paddingVertical: 60,
      paddingHorizontal: 40,
    },
    emptyText: {
      fontSize: theme.fontSize.medium,
      fontWeight: '600',
      color: theme.colors.text,
      textAlign: 'center',
      marginBottom: 8,
    },
    emptySubtext: {
      fontSize: theme.fontSize.small,
      color: theme.colors.greyBlue,
      textAlign: 'center',
    },
    buttonAdd: {
      height: undefined,
      minWidth: 250,
      marginTop: theme.spacing.sm,
      marginHorizontal: theme.spacing.lg,
      paddingVertical: theme.spacing.sm,
      backgroundColor: theme.colors.primary + '10',
      borderWidth: 1,
      borderColor: theme.colors.primary,
    },
  })
